/**
 * Header and footer.
 *
 * The header carries the account state, because "entrar" and "minha conta" are the
 * only two things that change across the whole site and they belong where the eye
 * already is.
 *
 * The bar itself is the first Liquid Glass surface on the page: a translucent sheet
 * over the scrolling hero, which is what puts the material in front of the reader
 * before they have read a word. It stays 56px tall on purpose. A header that takes
 * 15% of the viewport is a header that has become the page.
 *
 * ## Three languages, two places each
 *
 * Every piece of copy below is read from the dictionary for the language the address
 * bar is currently in, so there is no string in this file to fall out of step with
 * the page. The language switcher appears twice, in the header and inside the mobile
 * menu, because a site that offers English on a laptop and only Portuguese on a phone
 * is a site nobody can navigate on a phone.
 */

import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';

import { useAuth } from '../auth/AuthProvider';
import { CONTACT_EMAIL, CONTACT_MAILTO } from '../content/contact';
import { format, useI18n } from '../i18n';
import { Link, useRouter, type Route } from '../router';
import { ForMacOS } from './ForMacOS';
import { LanguageSwitcher } from './LanguageSwitcher';

/**
 * The two navigation destinations, named by the key their label lives under.
 *
 * `key` rather than `label`: the label is a word that changes with the language, and
 * an array of strings would need one array per language. Indexing the dictionary by
 * key means the destination list is written once and stays correct in all three.
 */
const NAV: readonly { key: 'navApps' | 'navPlans' | 'navSkins'; to: Route }[] = [
  { key: 'navApps', to: 'home' },
  { key: 'navPlans', to: 'planos' },
  { key: 'navSkins', to: 'skinNFT' },
];

export const Navbar: React.FC = () => {
  const { route } = useRouter();
  const { status, session } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-ink/70 backdrop-blur-2xl backdrop-saturate-150">
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-4 px-5 sm:px-8">
        {/*
          The wordmark and the platform badge are one group. Left as siblings, the
          `justify-between` below would read the badge as a third column and shove
          the navigation off centre, which looks like a layout mistake rather than
          a decision.
        */}
        <div className="flex items-center gap-3">
          <Link
            to="home"
            className="flex items-center gap-2.5 text-cream transition-opacity hover:opacity-80"
            onNavigate={() => setOpen(false)}
          >
            <img src="/OrobasWorkLogoNewGold.svg" alt={t.chrome.brandName} width={20} height={20} className="block" />
            <span className="text-[15px] font-semibold tracking-tight">{t.chrome.brandName}</span>
          </Link>

          <ForMacOS />
        </div>

        <nav className="hidden items-center gap-8 sm:flex" aria-label={t.chrome.navMain}>
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              aria-current={route === item.to ? 'page' : undefined}
              className={`text-[13px] transition-colors ${
                route === item.to ? 'text-cream' : 'text-muted hover:text-cream'
              }`}
            >
              {t.chrome[item.key]}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          {/* Hidden below `sm` because the mobile menu carries it, and two copies of
              the same control visible at once is one too many. */}
          <LanguageSwitcher className="hidden items-center gap-0.5 sm:flex" />

          {status === 'signed-in' ? (
            <Link
              to="conta"
              aria-current={route === 'conta' ? 'page' : undefined}
              className="pill pressable border border-line-strong px-4 py-1.5 text-[13px] text-cream hover:bg-white/10"
            >
              {session?.email?.split('@')[0] ?? t.chrome.myAccount}
            </Link>
          ) : (
            <Link
              to="conta"
              aria-current={route === 'conta' ? 'page' : undefined}
              className="pill pressable bg-gold px-4 py-1.5 text-[13px] text-ink hover:bg-gold-bright"
            >
              {t.chrome.signIn}
            </Link>
          )}

          <button
            type="button"
            onClick={() => setOpen((previous) => !previous)}
            aria-expanded={open}
            aria-controls="menu-mobile"
            aria-label={open ? t.chrome.closeMenu : t.chrome.openMenu}
            className="pressable rounded-full p-2 text-muted transition-colors hover:text-cream sm:hidden"
          >
            {open ? (
              <X className="size-5" aria-hidden="true" />
            ) : (
              <Menu className="size-5" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {open && (
        <nav id="menu-mobile" className="border-t border-line px-5 pb-3 sm:hidden" aria-label={t.chrome.navMain}>
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              aria-current={route === item.to ? 'page' : undefined}
              className="block py-2.5 text-sm text-muted transition-colors hover:text-cream"
              onNavigate={() => setOpen(false)}
            >
              {t.chrome[item.key]}
            </Link>
          ))}
          <Link
            to="conta"
            aria-current={route === 'conta' ? 'page' : undefined}
            className="block py-2.5 text-sm text-muted transition-colors hover:text-cream"
            onNavigate={() => setOpen(false)}
          >
            {status === 'signed-in' ? t.chrome.myAccount : t.chrome.signIn}
          </Link>

          {/*
            Below the page links rather than beside them: language is a preference,
            not a destination, and a reader looking for it has already looked in the
            header and not found it on a phone.
          */}
          <div className="mt-2 flex items-center gap-1 border-t border-line pt-3">
            <LanguageSwitcher className="flex items-center gap-0.5" />
          </div>
        </nav>
      )}
    </header>
  );
};

export const Footer: React.FC = () => {
  const { t } = useI18n();

  return (
    <footer className="border-t border-line px-5 py-14 sm:px-8">
      <div className="mx-auto flex max-w-6xl flex-col gap-8 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2.5 text-cream">
            <img src="/OrobasWorkLogoNewGold.svg" alt={t.chrome.brandName} width={18} height={18} className="block" />
            <span className="text-[15px] font-semibold tracking-tight">{t.chrome.brandName}</span>
          </div>
          <p className="mt-3.5 max-w-xs text-xs leading-relaxed text-muted">{t.footer.blurb}</p>
        </div>

        <div className="text-xs leading-relaxed text-muted sm:text-right">
          <p>
            {/*
              `href` and the text come from one constant. They used to be written
              separately, the address inline and the label out of a dictionary, which
              is two things to change when the address changes and one of them easy to
              miss.
            */}
            <a href={CONTACT_MAILTO} className="text-cream transition-colors hover:text-gold">
              {CONTACT_EMAIL}
            </a>
          </p>
          <p className="mt-1.5">{t.footer.paymentsLine}</p>

          {/*
            The parent company, and the legal notice it owns.

            `rel="noopener noreferrer"` is not decoration. Without `noopener` the page
            this opens gets a handle on `window.opener` and can navigate this tab
            somewhere else after the reader comes back, which on a page with a sign-in
            form is the oldest trick there is.

            The logo is an `<img>` rather than inline SVG for a second reason: the file
            carries global ids (`_Radial1`, `_clip2`) and inlining it would put those
            into this document's id space, where they could collide with anything else.
            An `<img>` gets its own document and cannot collide with anything.

            `width`/`height` are declared so the browser reserves the box before the
            file arrives. Without them the footer reflows as the logo lands.
          */}
          <div className="mt-5 flex items-start justify-end gap-2.5">
            <a
              href="https://www.m2on.org"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 rounded-[6px] transition-opacity hover:opacity-80"
            >
              <img
                src="/m2on.svg"
                alt={t.footer.companyLinkLabel}
                width={28}
                height={28}
                loading="lazy"
                decoding="async"
                className="block size-7"
              />
            </a>

            <div className="sm:text-right">
              <p>
                {format(t.footer.rights, {
                  year: new Date().getFullYear(),
                  company: t.footer.companyName,
                  legalId: t.footer.legalId,
                })}
              </p>
              <p className="mt-1">{t.footer.reserved}</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};